import React from "react";
import { useSettingsAccess } from "./SettingsAccessContext";

interface EditableCardFooterProps {
  /** Whether the card is currently unlocked for editing. */
  editing:   boolean;
  /** Whether a save is in flight (disables the buttons). */
  saving:    boolean;
  onEdit:    () => void;
  onSave:    () => void;
  onCancel:  () => void;
  /** Override the primary button label (defaults to "Save Changes"). */
  saveLabel?: string;
}

/**
 * Shared footer for settings cards. Shows a single "Edit" button while the card
 * is locked, then swaps to "Save Changes" + "Cancel" once the user opts in.
 * Gating every save behind an explicit Edit click guards against accidentally
 * pushing changes the user never meant to make.
 */
export const EditableCardFooter: React.FC<EditableCardFooterProps> = ({
  editing,
  saving,
  onEdit,
  onSave,
  onCancel,
  saveLabel = "Save Changes",
}) => {
  const { canEdit } = useSettingsAccess();

  // View-only grant on this section: no way into edit mode at all. Rendering a
  // disabled Edit button instead would invite a click that can only ever 403.
  if (!canEdit && !editing) {
    return (
      <p className="text-xs text-gray-400">
        You have view-only access to this section.
      </p>
    );
  }

  return editing ? (
    <div className="flex items-center gap-3">
      <button
        onClick={onSave}
        disabled={saving}
        className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {saving ? "Saving…" : saveLabel}
      </button>
      <button
        onClick={onCancel}
        disabled={saving}
        className="px-5 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
      >
        Cancel
      </button>
    </div>
  ) : (
    <div className="flex justify-start">
      <button
        onClick={onEdit}
        className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        Edit
      </button>
    </div>
  );
};
